<template>
  <Modal
    :model-value="opened"
    v-on:update:model-value="
      (value) => {
        opened = value
      }
    "
    title="Update selected elements"
  >
    <p class="content">
      Select the properties to change, then select the value to set on every element.<br />
      Only the selected properties will be updated.
    </p>
    <form
      class="form"
      id="update-selection"
      v-on:submit.prevent="update"
    >
      <div class="field">
        <label class="checkbox label">
          <input
            type="checkbox"
            v-model="fields.type_id.enabled"
            :disabled="loading || undefined"
          />
          Change element type
        </label>
        <div class="control">
          <div class="select is-fullwidth">
            <select
              class="is-truncated"
              v-model="fields.type_id.value"
              :disabled="!fields.type_id.enabled || loading || undefined"
            >
              <option :value="null">&mdash;</option>
              <option
                v-for="type in corpus.types"
                :key="type.id"
                :value="type.id"
                :title="type.display_name"
              >
                {{ type.display_name }}
              </option>
            </select>
          </div>
        </div>
        <p
          class="help is-danger"
          v-for="err in fieldErrors.type_id ?? []"
          :key="err"
        >
          {{ err }}
        </p>
      </div>
      <div class="field">
        <label class="checkbox label">
          <input
            type="checkbox"
            v-model="fields.rotation_angle.enabled"
            :disabled="loading || undefined"
          />
          Set rotation angle
        </label>
        <div class="control">
          <div class="select is-fullwidth">
            <select
              v-model="fields.rotation_angle.value"
              :disabled="!fields.rotation_angle.enabled || loading || undefined"
            >
              <option :value="null">&mdash;</option>
              <option
                v-for="rotation in ALLOWED_ROTATION_ANGLES"
                :key="rotation"
                :value="rotation"
              >
                {{ rotation }}°
              </option>
            </select>
          </div>
        </div>
        <p
          class="help is-danger"
          v-for="err in fieldErrors.rotation_angle ?? []"
          :key="err"
        >
          {{ err }}
        </p>
      </div>
      <div class="field">
        <label class="checkbox label">
          <input
            type="checkbox"
            v-model="fields.mirrored.enabled"
            :disabled="loading || undefined"
          />
          Mirror image
        </label>
        <div class="control">
          <input
            type="radio"
            v-model="fields.mirrored.value"
            :disabled="!fields.mirrored.enabled || loading || undefined"
            :value="true"
          />
          Yes
          <input
            type="radio"
            v-model="fields.mirrored.value"
            :disabled="!fields.mirrored.enabled || loading || undefined"
            :value="false"
          />
          No
        </div>
        <p
          class="help is-danger"
          v-for="err in fieldErrors.mirrored ?? []"
          :key="err"
        >
          {{ err }}
        </p>
      </div>
    </form>
    <div
      class="content"
      v-if="!updateDisabled"
    >
      <hr />
      <p>
        The selected elements in <strong>{{ corpus.name }}</strong> will be updated:
      </p>
      <ul>
        <li v-if="(payload.type_id ?? null) !== null">
          Type:
          {{ Object.values(corpus.types).find(({ id }) => id === payload.type_id)?.display_name }}
        </li>
        <li v-if="(payload.rotation_angle ?? null) !== null">
          Rotation angle: {{ payload.rotation_angle }}°
        </li>
        <li v-if="(payload.mirrored ?? null) !== null">
          Mirrored image: {{ payload.mirrored ? 'Yes' : 'No' }}
        </li>
      </ul>
    </div>
    <template v-slot:footer="{ close }">
      <button
        class="button"
        type="button"
        v-on:click="close"
      >
        Cancel
      </button>
      <button
        class="button is-primary"
        :class="{ 'is-loading': loading }"
        type="submit"
        form="update-selection"
        :disabled="updateDisabled"
      >
        Apply changes
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { type SelectionUpdatePayload } from '@/api/selection.ts'
import { ALLOWED_ROTATION_ANGLES } from '@/config.ts'
import { errorParser, extractFieldErrors, isArkindexError } from '@/helpers/error.ts'
import { useCorporaStore } from '@/stores/corpora.ts'
import { useNotificationStore } from '@/stores/notification.ts'
import { useSelectionStore } from '@/stores/selection.ts'
import type { UUID } from '@/types/index.ts'

import Modal from '../Modal.vue'

const opened = defineModel<boolean>()

type Fields = {
  // This ensures that we are not missing any fields implemented in UpdateSelection
  [k in keyof Required<SelectionUpdatePayload>]: {
    // Whether the user has chosen to update this field on all elements
    enabled: boolean
    // The currently selected value for this field, nullable
    value: SelectionUpdatePayload[k]
  }
}
const fields = ref<Fields>({
  type_id: { enabled: false, value: null },
  rotation_angle: { enabled: false, value: null },
  mirrored: { enabled: false, value: false },
})
const fieldErrors = ref<{ [k in keyof SelectionUpdatePayload]: string[] }>({})

/**
 * Payload to send to the updateSelection API helper
 */
const payload = computed(
  (): SelectionUpdatePayload =>
    Object.fromEntries(
      Object.entries(fields.value)
        // Only includes enabled fields with a selected value
        .filter(([, { enabled, value }]) => enabled && value !== null)
        // Extract just the value
        .map(([key, { value }]) => [key, value]),
    ),
)

const { corpusId } = defineProps<{ corpusId: UUID }>()
const corporaStore = useCorporaStore()
const selectionStore = useSelectionStore()
const notificationStore = useNotificationStore()

const corpus = computed(() => corporaStore.corpora[corpusId])

/**
 * Disable the submit button anytime there are no fields to update, or no valid values for the fields to update
 */
const updateDisabled = computed(
  (): true | undefined =>
    loading.value ||
    Object.values(payload.value).every((value) => value === null || value === undefined) ||
    undefined,
)

const loading = ref(false)

async function update() {
  if (updateDisabled.value) return
  fieldErrors.value = {}
  loading.value = true
  try {
    await selectionStore.update(corpusId, payload.value)
    opened.value = false
  } catch (error) {
    let otherErrors: string[]

    if (isArkindexError(error)) {
      ;[fieldErrors.value, otherErrors] = extractFieldErrors(
        error.response.data,
        Object.keys(fields.value),
      )
    } else {
      otherErrors = [errorParser(error)]
    }

    if (otherErrors.length) {
      notificationStore.notify({
        type: 'error',
        text: `An error occurred updating your account: ${otherErrors.join(' - ')}`,
      })
    }
  } finally {
    loading.value = false
  }
}
</script>
